import { useEffect, useMemo } from 'react';

import { getServiceId } from '@/api/tokens.helper';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store';
import { useImportedTransactionsStore } from '@/store/expenses-store/transactions/imported-transactions.store.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store';
import { useIsMobile } from '@hooks/useIsMobile.tsx';
import { DesktopTransactions } from '@pages/Expenses/TransactionsPage/blocks/DesktopTransactions.tsx';
import { MobileTransactions } from '@pages/Expenses/TransactionsPage/blocks/MobileTransactions.tsx';
import { transformExpensesToRows } from '@pages/Expenses/TransactionsPage/utils/transformExpense.ts';

const TransactionsPage = () => {
  const isMobile = useIsMobile();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.wallet.Transactions);
  // Categories sit behind wallet's basic transaction right — the same one the category
  // endpoints ask for, and the one the budget page gates its category actions on.
  const canManageCategories = can(PERMISSIONS.wallet.TransactionsBasic);
  const {
    transactions,
    loading,
    error,
    getAllTransactions,
    transactionCategories,
    getTransactionsCategories,
  } = useTransactionsStore();
  const isCsvImporting = useImportedTransactionsStore((s) => s.isCsvImporting);
  const { invoices, getInvoices } = useInvoice();

  const vesselId = getServiceId();
  const spQuery = useServiceProvidersQuery(100, { vesselId, search: '' });

  const serviceProvidersMap = useMemo(() => {
    const serviceProviders = spQuery.data?.pages?.flatMap((p) => p.items) ?? [];
    const map = new Map<string, (typeof serviceProviders)[number]>();
    for (const sp of serviceProviders) {
      map.set(sp.id, sp);
    }
    return map;
  }, [spQuery.data?.pages]);

  const invoicesMap = useMemo(() => {
    const map = new Map<string, (typeof invoices)[number]>();
    for (const inv of invoices) {
      map.set(inv.id, inv);
    }
    return map;
  }, [invoices]);

  useEffect(() => {
    void getAllTransactions();
    void getInvoices();
    void getTransactionsCategories();
  }, [getAllTransactions, getInvoices, getTransactionsCategories]);

  const rows = useMemo(
    () =>
      transformExpensesToRows(
        transactions,
        serviceProvidersMap,
        invoicesMap,
        transactionCategories,
      ),
    [transactions, serviceProvidersMap, invoicesMap, transactionCategories],
  );

  return (
    <>
      {isMobile ? (
        <MobileTransactions
          data={rows}
          loading={loading}
          error={error}
          isCsvImporting={isCsvImporting}
          canManage={canManage}
          canManageCategories={canManageCategories}
        />
      ) : (
        <DesktopTransactions
          data={rows}
          expenses={transactions}
          loading={loading}
          error={error}
          isCsvImporting={isCsvImporting}
          canManage={canManage}
          canManageCategories={canManageCategories}
        />
      )}
    </>
  );
};

export default TransactionsPage;
